import { motion } from "framer-motion";
import {
  Recycle,
  Leaf,
  Cpu,
  ShieldCheck,
  ArrowRight,
} from "lucide-react";
import "./css/innovation.css";

export default function Innovation() {
  return (
    <div className="innovation-page">

      {/* HERO */}
      <section className="hero">
        <motion.h1
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          Innovation Rooted in
          <span>Real Farming Challenges</span>
        </motion.h1>

        <p>
          Green Knowledge delivers climate-resilient agricultural innovations
          designed for cyclone-prone regions of Tamil Nadu.
        </p>
      </section>

      {/* TIMELINE */}
      <section className="timeline">

        {/* Fiber Sticks */}
        <motion.div
          className="timeline-item"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <div className="timeline-icon">
            <Recycle />
          </div>
          <div className="card">
            <h3>Fiber Sticks: Waste to Wealth</h3>

            <p>
              <strong>Problem:</strong> Every year, cyclones in Tamil Nadu damage
              banana plantations as strong winds bend or break plants. Farmers
              rely on wooden support sticks that are expensive, single-use, and
              contribute to deforestation and farm waste, increasing production
              costs season after season.
            </p>

            <p>
              <strong>Solution:</strong> Green Knowledge is developing reusable
              Fiber Sticks made from organic fiber and resin. These sticks are
              lightweight, durable, cyclone-resistant, and reusable for multiple
              seasons. They help farmers reduce costs, reuse damaged plant
              material, and minimize environmental waste. The design is being
              refined in collaboration with experts at MIT to ensure soil safety
              and long-term performance in cyclone-prone regions.
            </p>
          </div>
        </motion.div>

        {/* Tissue Culture */}
        <motion.div
          className="timeline-item"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <div className="timeline-icon">
            <Leaf />
          </div>
          <div className="card">
            <h3>Tissue Culture Banana Plant</h3>


            <h4>The Problem</h4>

            <p>
              Traditional banana farming depends on
              suckers taken from existing plants, which often carry hidden
              diseases. This leads to uneven growth, poor yields, and
              unpredictable harvests, especially in climate-stressed regions.
            </p>
            <p>
              Reusing infected banana rhizomes spreads
              diseases such as Fusarium wilt and bacterial wilt across fields.
              This causes uneven growth, high chemical usage, increased costs,
              and in many cases complete crop failure.
            </p>

            <h4>Our Solution</h4>

            <p>
              Tissue culture banana plants are grown in
              controlled laboratory conditions, ensuring disease-free and
              uniform planting material. By integrating tissue culture plants
              with other Green Knowledge innovations, farmers achieve consistent
              growth, better yields, and reliable year-round production.
            </p>
            <p>
              Replacing infected planting material
              with clean, lab-grown tissue culture plants breaks the disease
              cycle at its source, protecting fields and restoring farm
              stability without excessive chemical dependence.
            </p>

            <p>
              Green Knowledge produces tissue culture banana plants using clean
              meristem cells in sterile laboratory environments. This ensures
              zero pathogen presence. These plants grow uniformly, mature 20–30%
              faster, and deliver 20–50% higher yields compared to traditional
              farming methods.
            </p>
            <p>
              Contact us at 39 East Street, M. Puthur, Cuddalore 607004 or
              +91-8675221100
            </p>
          </div>
        </motion.div>

        {/* IoT Farming */}
        <motion.div
          className="timeline-item"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <div className="timeline-icon">
            <Cpu />
          </div>
          <div className="card">
            <h3>
              Climate-Adaptive IoT Farming: Precision Irrigation & Nutrition for
              Changing Seasons
            </h3>

            <p>
              <strong>Problem:</strong> Climate change has disrupted traditional
              farming cycles. Erratic rainfall, droughts, and cyclones make
              irrigation and nutrient management unpredictable, resulting in
              water wastage and reduced yields.
            </p>

            <p>
              <strong>Solution:</strong> Green Knowledge’s IoT-based farming
              system deploys smart sensors to monitor soil moisture, NPK levels,
              pH, temperature, and humidity in real time. AI-driven insights
              deliver precise irrigation and nutrition recommendations,
              reducing water usage by 30–50% while improving crop performance.
            </p>

            <p>
              Proven impact includes scalable pilots in Cuddalore using
              affordable ESP8266/NodeMCU systems and mobile alerts such as
              “Irrigate 20L/plant now” or “Delay irrigation due to incoming
              rain.”
            </p>
          </div>
        </motion.div>
        <motion.div
          className="timeline-item"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <div className="timeline-icon">
            <Cpu />
          </div>
          <div className="card">
<h3>Key Benefits</h3>
            <ul>
              <li>
                <strong>Disease-Free Assurance:</strong> Prevents rhizome-borne
                infections and reduces chemical usage by up to 50%
              </li>
              <li>
                <strong>Higher Yield & Quality:</strong> Uniform growth leads to
                synchronized harvests, higher bunch weights, and increased
                profitability
              </li>
              <li>
                <strong>Cyclone-Resilient Farming:</strong> Works seamlessly with
                reusable fiber sticks to create storm-resistant banana
                plantations
              </li>
            </ul>
          </div>
        </motion.div>
      </section>

      {/* CTA */}
      <section className="cta">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          Cultivate Wisdom, Growing Nature.
        </motion.h2>

        <p>
          Transform your farm with Green Knowledge’s climate-smart solutions.
          Contact us at 39 East Street, M. Puthur, Cuddalore 607004 or
          +91-8675221100 for pilots and custom demos.
        </p>

        <button className="cta-button">
          Get Started <ArrowRight size={16} />
        </button>
      </section>
    </div>
  );
}
